<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-4">
        <h2 class="font-weight-bolder">{{ pageTitle }}</h2>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined class="mr-2" @click="goBack">
          {{ $t('back') }}
        </v-btn>
      </div>

      <v-card v-if="detail" elevation="0" class="px-5">
        <v-card-text class="text-body-1 black--text pa-0">
          <div class="px-4 py-4">
            <!-- Progress summary -->
            <div class="bulk-result-progress-section">
              <span class="bulk-result-progress-title text--secondary">
                {{ $t('memberDataSection.processingBatchBulkImport.importCompleted') }}
              </span>
              <div class="bulk-result-progress-row mt-2">
                <v-progress-linear
                  rounded
                  height="12"
                  :value="importProgressPercent"
                  color="kenyaGreen"
                  background-color="green2"
                  class="bulk-result-progress-bar"
                ></v-progress-linear>
                <span
                  class="bulk-result-progress-percent kenyaGreen--text"
                >{{ importProgressPercent }}%</span>
              </div>
              <p class="bulk-result-progress-status text--secondary mt-2 mb-0">
                <span class="font-weight-bold">{{ importedRecordsCount }}</span>
                {{ $t('of') }}
                <span class="font-weight-bold">{{ detail.totalRecordsCount }}</span>
                {{ $t('recordsImported') }}
              </p>
            </div>

            <!-- Summary cards -->
            <v-row class="bulk-result-summary-row mt-4" dense>
              <v-col v-for="card in summaryCards" :key="card.key" cols="12" sm="4">
                <div
                  class="bulk-result-summary-card"
                  :style="{ borderLeftColor: themeColor(card.color) }"
                >
                  <div
                    class="bulk-result-summary-card__value"
                    :style="{ color: themeColor(card.color) }"
                  >
                    {{ card.count }}
                  </div>
                  <div class="bulk-result-summary-card__label text--secondary">{{ card.label }}</div>
                </div>
              </v-col>
            </v-row>

            <!-- Filter chips (one per error category) -->
            <div class="bulk-result-filter-chips mt-5 d-flex flex-wrap">
              <button
                v-for="chip in filterChips"
                :key="chip.value"
                type="button"
                class="bulk-result-filter-chip"
                :style="chipStyle(chip)"
                @click="resultFilter = chip.value"
              >
                <v-icon
                  v-if="chip.icon"
                  small
                  :color="chipIconColor(chip)"
                  class="mr-1"
                >
                  {{ chip.icon }}
                </v-icon>
                {{ chip.label }} ({{ chip.count }})
              </button>
            </div>

            <!-- Bulk resolution alert -->
            <div
              v-if="issuesRecordsCount > 0"
              class="bulk-result-issues-alert mt-4"
              :style="{
                backgroundColor: themeColor('secondary2light'),
                borderColor: themeColor('third'),
              }"
            >
              <p class="bulk-result-issues-alert__heading black--text mb-2">
                {{ $t('memberDataSection.processingBatchBulkImport.issuesDetectedHeading') }}
              </p>
              <p class="bulk-result-issues-alert__body mb-3">
                {{ $t('memberDataSection.processingBatchBulkImport.issuesDetectedDescription') }}
              </p>
              <v-checkbox
                v-model="deleteDiscrepancyRows"
                hide-details
                class="bulk-result-issues-alert__checkbox mt-0 pt-0"
                color="third"
                :disabled="isLocked || actionLoading"
                @change="onDeleteDiscrepancyRowsChange"
              >
                <template v-slot:label>
                  <span class="text-body-2 black--text">
                    {{ $t('memberDataSection.processingBatchBulkImport.deleteRowsWithIssues') }}
                  </span>
                </template>
              </v-checkbox>
            </div>

            <!-- Records table (rows clickable → detail dialog) -->
            <v-card class="py-0 mt-4 bulk-result-table-card" elevation="1">
              <v-data-table
                item-key="id"
                disable-pagination
                :headers="tableHeaders"
                :items="filteredRecords"
                hide-default-footer
                class="bulk-result-table"
                @click:row="openDetail"
              >
                <template v-slot:item.rowNumber="{ item }">
                  {{ rowNumber(item) }}
                </template>
                <template v-slot:item.startDate="{ item }">
                  {{ cell(item, "processDate") || cell(item, "startDate") }}
                </template>
                <template v-slot:item.purchaseOrderCodes="{ item }">
                  <div class="d-flex align-center">
                    <span
                      :class="{ 'red--text': isFieldError(item, 'orderIds') || isFieldError(item, 'purchaseDate') }"
                      class="text-body-2"
                    >
                      {{ orderCodesDisplay(item) }}
                    </span>
                    <v-tooltip v-if="isFieldError(item, 'orderIds') || isFieldError(item, 'purchaseDate')" bottom max-width="360">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" small color="third" class="ml-1">mdi-alert</v-icon>
                      </template>
                      <div class="bulk-result-issue-tooltip">
                        <div class="font-weight-bold">{{ getRecordIssueTitle(item) }}</div>
                        <div class="mt-1">{{ getRecordIssueDescription(item) }}</div>
                      </div>
                    </v-tooltip>
                  </div>
                </template>
                <template v-slot:item.processType="{ item }">
                  <div class="d-flex align-center">
                    <span :class="{ 'red--text': isFieldError(item, 'processType') }" class="text-body-2">
                      {{ cell(item, "processType") }}
                    </span>
                    <v-tooltip v-if="isFieldError(item, 'processType')" bottom max-width="360">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" small color="third" class="ml-1">mdi-alert</v-icon>
                      </template>
                      <div class="bulk-result-issue-tooltip">
                        <div class="font-weight-bold">{{ getRecordIssueTitle(item) }}</div>
                        <div class="mt-1">{{ getRecordIssueDescription(item) }}</div>
                      </div>
                    </v-tooltip>
                  </div>
                </template>
                <template v-slot:item.grade="{ item }">
                  {{ cell(item, "grade") }}
                </template>
                <template v-slot:item.season="{ item }">
                  {{ cell(item, "season") }}
                </template>
                <template v-slot:item.status="{ item }">
                  <v-btn
                    small
                    rounded
                    depressed
                    min-width="140"
                    height="34"
                    class="bulk-result-status-btn non-clickable"
                    :style="statusPillStyle(item)"
                  >
                    {{ statusLabel(item) }}
                  </v-btn>
                </template>
                <template v-slot:item.action="{ item }">
                  <div class="d-flex align-center justify-center flex-nowrap bulk-result-actions">
                    <v-btn
                      icon
                      small
                      elevation="2"
                      class="mr-1"
                      :disabled="isLocked || actionLoading"
                      @click.stop="editRecord(item)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      elevation="2"
                      :disabled="isLocked || actionLoading"
                      @click.stop="deleteRecord(item)"
                    >
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card>

            <v-divider class="my-5 px-0"></v-divider>
            <v-card-actions class="px-0">
              <v-spacer></v-spacer>
              <v-btn
                outlined
                color="primary"
                class="mr-2"
                width="112"
                :disabled="isLocked || actionLoading || importLoading"
                @click="cancelImportAction"
              >
                {{ $t('cancel') }}
              </v-btn>
              <v-btn
                color="primary"
                class="btn-11 btn-22 white--text"
                :disabled="!canImport"
                :loading="importLoading"
                @click="runImportAction"
              >
                {{ $t('oma.import') }}
              </v-btn>
            </v-card-actions>
          </div>
        </v-card-text>
      </v-card>

      <v-card v-else-if="!loading" elevation="0" class="px-5 pa-8 text-center text--secondary">
        {{ $t('memberDataSection.processingBatchBulkImport.noDataFound') }}
        <div class="mt-4">
          <v-btn color="primary" outlined @click="goBack">
            {{ $t('back') }}
          </v-btn>
        </div>
      </v-card>

      <ProcessingBatchBulkImportDetailDialog
        v-model="detailDialog"
        :record="selectedRecord"
        :issue-title="selectedRecord ? getRecordIssueTitle(selectedRecord) : ''"
        :issue-description="selectedRecord ? getRecordIssueDescription(selectedRecord) : ''"
        @edit="onDetailEdit"
      />

      <ProcessingBatchBulkImportEditDialog
        v-model="editDialog"
        :record="editingRecord"
        :loading="actionLoading"
        @save="onEditSave"
      />

      <ConfirmBox
        :dialog="confirm.dialog"
        :title="confirm.title"
        :message="confirm.message"
        :confirmBtnText="confirm.confirmBtnText"
        :cancelBtnText="$t('cancel')"
        @cancel:action="onConfirmCancel"
        @continue:action="onConfirmContinue"
      />

      <ProcessingBatchBulkImportSuccessDialog
        v-model="successDialog"
        :count="importedCount"
        @close="onSuccessClose"
      />

      <v-overlay :value="actionLoading || importLoading || loading" z-index="300">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-container>
  </div>
</template>

<script>
import {
  isDataDiscrepancyRecord,
  isSuccessRecord,
  isFailedRecord,
  hasFieldError,
  mapServerRecord,
  mapServerImport,
  SERVER_ERROR_CODE_I18N,
  SERVER_ERROR_CODE_LABEL_I18N,
  SERVER_BULK_IMPORT_STATUS,
} from "@/utils/processingBatchBulkImport";
import ProcessingBatchBulkImportService from "@/_services/ProcessingBatchBulkImportService";
import ProcessingBatchBulkImportEditDialog from "./ProcessingBatchBulkImportEditDialog.vue";
import ProcessingBatchBulkImportDetailDialog from "./ProcessingBatchBulkImportDetailDialog.vue";
import ProcessingBatchBulkImportSuccessDialog from "./ProcessingBatchBulkImportSuccessDialog.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";

const RESULT_FILTER = { ALL: "all", SUCCESS: "success" };
const B = "memberDataSection.processingBatchBulkImport";

export default {
  name: "ProcessingBatchBulkImportResult",
  components: {
    ProcessingBatchBulkImportEditDialog,
    ProcessingBatchBulkImportDetailDialog,
    ProcessingBatchBulkImportSuccessDialog,
    ConfirmBox,
  },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      buyingStationId: this.$route.params.buyingStationId,
      detail: null,
      bulkImportId: null,
      resultFilter: RESULT_FILTER.ALL,
      deleteDiscrepancyRows: false,
      loading: false,
      actionLoading: false,
      importLoading: false,
      detailDialog: false,
      selectedRecord: null,
      editDialog: false,
      editingRecord: null,
      successDialog: false,
      importedCount: 0,
      confirm: { dialog: false, title: "", message: "", confirmBtnText: "", handler: null },
      tableHeaders: [
        { text: this.$t(`${B}.rowCol`),         value: "rowNumber",  width: "60px", class: "bulk-result-table-header" },
        { text: this.$t(`${B}.startDateCol`),   value: "startDate",  class: "bulk-result-table-header" },
        { text: this.$t(`${B}.purchaseOrdersCol`), value: "purchaseOrderCodes", class: "bulk-result-table-header" },
        { text: this.$t(`${B}.processTypeCol`), value: "processType", class: "bulk-result-table-header" },
        { text: this.$t(`${B}.gradeCol`),       value: "grade",      class: "bulk-result-table-header" },
        { text: this.$t(`${B}.seasonCol`),      value: "season",     class: "bulk-result-table-header" },
        { text: this.$t("oma.status"),           value: "status",     align: "center", class: "bulk-result-table-header" },
        { text: this.$t("oma.action"),           value: "action",     align: "center", sortable: false, class: "bulk-result-table-header bulk-result-action-col", width: "120px" },
      ],
    };
  },
  computed: {
    isLocked() {
      return (
        this.detail?.status === SERVER_BULK_IMPORT_STATUS.CANCELLED ||
        this.detail?.status === SERVER_BULK_IMPORT_STATUS.SUCCESS
      );
    },
    canImport() {
      return (
        !this.isLocked &&
        !this.actionLoading &&
        !this.importLoading &&
        Boolean(this.bulkImportId) &&
        this.successRecordsCount > 0 &&
        // Every record must be resolved to SUCCESS — no outstanding issues.
        this.issuesRecordsCount === 0
      );
    },
    pageTitle() {
      return this.$t(`${B}.title`);
    },
    breadcrumbs() {
      return [
        { text: this.$t("dashboard"), disabled: false, to: { name: "AdminDashboard" }, exact: true },
        { text: this.$t("memberDataSection.buyingStation"), disabled: true, href: "#" },
        { text: this.pageTitle, disabled: true, href: "#" },
      ];
    },
    allRecords() {
      return this.detail?.records || [];
    },
    successRecords() {
      return this.allRecords.filter((r) => isSuccessRecord(r));
    },
    failedRecords() {
      return this.allRecords.filter((r) => isFailedRecord(r));
    },
    discrepancyRecords() {
      return this.allRecords.filter((r) => isDataDiscrepancyRecord(r));
    },
    importedRecordsCount() {
      if (this.detail?.importedRecordsCount != null) return this.detail.importedRecordsCount;
      return this.successRecords.length;
    },
    importProgressPercent() {
      const total = this.detail?.totalRecordsCount || 0;
      if (!total) return 0;
      return Math.min(100, Math.round((this.importedRecordsCount / total) * 100));
    },
    totalRecordsCount() {
      return this.detail?.totalRecordsCount || 0;
    },
    successRecordsCount() {
      return this.detail?.successRecordsCount ?? this.successRecords.length;
    },
    issuesRecordsCount() {
      return this.detail?.discrepancyRecordsCount ?? this.discrepancyRecords.length;
    },
    /** Count of records that carry each distinct error code. */
    errorCodeCounts() {
      const counts = {};
      this.discrepancyRecords.forEach((r) => {
        (r.errorCodes || []).forEach((code) => {
          counts[code] = (counts[code] || 0) + 1;
        });
      });
      return counts;
    },
    summaryCards() {
      const t = (key) => this.$t(`${B}.${key}`);
      return [
        { key: "total", color: "gray", label: t("tabTotalRecords"), count: this.totalRecordsCount },
        { key: "success", color: "kenyaGreen", label: t("tabSuccess"), count: this.successRecordsCount },
        { key: "issues", color: "third", label: t("tabIssues"), count: this.issuesRecordsCount },
      ];
    },
    filterChips() {
      const t = (key) => this.$t(`${B}.${key}`);
      const chips = [
        { value: RESULT_FILTER.ALL, label: t("filterAll"), count: this.totalRecordsCount, icon: "mdi-check", color: "kenyaGreen" },
        { value: RESULT_FILTER.SUCCESS, label: t("filterSuccess"), count: this.successRecordsCount, icon: "mdi-check", color: "kenyaGreen" },
      ];
      Object.keys(this.errorCodeCounts).forEach((code) => {
        const labelKey = SERVER_ERROR_CODE_LABEL_I18N[code];
        chips.push({
          value: code,
          label: labelKey ? t(labelKey) : code,
          count: this.errorCodeCounts[code],
          icon: "mdi-alert",
          color: "third",
        });
      });
      return chips;
    },
    filteredRecords() {
      if (this.resultFilter === RESULT_FILTER.SUCCESS) return this.successRecords;
      if (this.resultFilter === RESULT_FILTER.ALL) return this.allRecords;
      // A specific error-code filter.
      return this.allRecords.filter((r) => (r.errorCodes || []).includes(this.resultFilter));
    },
  },
  watch: {
    id() {
      this.fetchDetail();
    },
  },
  mounted() {
    this.fetchDetail();
  },
  methods: {
    async fetchDetail() {
      this.bulkImportId = this.id != null ? this.id : null;
      if (!this.bulkImportId) {
        this.detail = null;
        return;
      }
      this.loading = true;
      try {
        const res = await ProcessingBatchBulkImportService.getImportById(this.bulkImportId);
        if (res && res.success !== false && res.data) {
          this.detail = mapServerImport(res.data);
          this.bulkImportId = this.detail.bulkImportId || this.bulkImportId;
          if (this.detail.buyingStationId != null) {
            this.buyingStationId = this.detail.buyingStationId;
          }
        } else {
          this.detail = null;
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.detail = null;
        this.notifyError(err && err.message);
      } finally {
        this.loading = false;
      }
    },
    refreshDetailCounts() {
      if (!this.detail) return;
      this.detail.totalRecordsCount = this.detail.records.length;
      this.detail.successRecordsCount = this.successRecords.length;
      this.detail.failedRecordsCount = this.failedRecords.length;
      this.detail.discrepancyRecordsCount = this.discrepancyRecords.length;
      this.detail.errorRecordsCount = this.discrepancyRecords.length;
      this.detail.importedRecordsCount = this.successRecords.length;
    },
    themeColor(name) {
      const theme = this.$vuetify.theme;
      const variant = theme.dark ? "dark" : "light";
      return (theme.themes[variant] && theme.themes[variant][name]) || "";
    },
    rowNumber(item) {
      const idx = this.allRecords.indexOf(item);
      return idx >= 0 ? idx + 1 : "—";
    },
    cell(item, field) {
      const rec = item?.recordedJsonData || {};
      const val = rec[field];
      return val != null && String(val).trim() !== "" ? val : "—";
    },
    orderCodesDisplay(item) {
      const rec = item?.recordedJsonData || {};
      const codes = rec.orderCodes;
      if (Array.isArray(codes) && codes.length) return codes.join(", ");
      // Fallback: show purchaseDate used for lookup during initial import.
      return rec.purchaseDate || "—";
    },
    isFieldError(item, field) {
      if (!isDataDiscrepancyRecord(item)) return false;
      return hasFieldError(item, field);
    },
    getRecordIssueTitle() {
      return this.$t(`${B}.issuesDetected`);
    },
    getRecordIssueDescription(item) {
      const codes = item.errorCodes || [];
      const msgs = codes
        .map((code) => {
          const key = SERVER_ERROR_CODE_I18N[code];
          return key ? this.$t(`${B}.${key}`) : "";
        })
        .filter(Boolean);
      return msgs.join(" ");
    },
    statusLabel(item) {
      if (isDataDiscrepancyRecord(item)) {
        return this.$t(`${B}.issuesDetected`);
      }
      if (isSuccessRecord(item)) return this.$t("success");
      return this.$t("failed");
    },
    statusPillStyle(item) {
      let mainName = "red";
      let bgName = "red2";
      if (isDataDiscrepancyRecord(item)) {
        mainName = "third";
        bgName = "secondary2light";
      } else if (isSuccessRecord(item)) {
        mainName = "kenyaGreen";
        bgName = "green2";
      }
      const main = this.themeColor(mainName);
      return { color: main, backgroundColor: this.themeColor(bgName), border: `2px solid ${main}` };
    },
    chipStyle(chip) {
      const active = this.resultFilter === chip.value;
      if (active) {
        return { backgroundColor: this.themeColor(chip.color), color: this.themeColor("white") };
      }
      return { backgroundColor: this.themeColor("themeGrey"), color: this.themeColor("lightBlack") };
    },
    chipIconColor(chip) {
      return this.resultFilter === chip.value ? "white" : chip.color;
    },

    // ── Detail dialog ───────────────────────────────────────────────────
    openDetail(item) {
      this.selectedRecord = item;
      this.detailDialog = true;
    },
    onDetailEdit(record) {
      this.detailDialog = false;
      this.editRecord(record);
    },

    // ── Edit ────────────────────────────────────────────────────────────
    editRecord(item) {
      this.editingRecord = item;
      this.editDialog = true;
    },
    async onEditSave({ recordId, payload }) {
      this.actionLoading = true;
      try {
        const res = await ProcessingBatchBulkImportService.updateRecord(recordId, payload);
        if (res && res.success !== false && res.data) {
          this.replaceRecord(mapServerRecord(res.data));
          this.refreshDetailCounts();
          this.editDialog = false;
          this.editingRecord = null;
          this.$notify({
            type: "success",
            text: res.message || this.$t(`${B}.recordUpdatedSuccess`),
          });
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.actionLoading = false;
      }
    },
    replaceRecord(mapped) {
      if (!this.detail || !this.detail.records) return;
      const idx = this.detail.records.findIndex((r) => r.id === mapped.id);
      if (idx >= 0) this.$set(this.detail.records, idx, mapped);
    },

    // ── Delete ──────────────────────────────────────────────────────────
    deleteRecord(item) {
      this.openConfirm({
        title: this.$t(`${B}.deleteRecord`),
        message: this.$t(`${B}.deleteRecordConfirm`),
        confirmBtnText: this.$t("delete"),
        handler: () => this.performDelete([item.id]),
      });
    },
    onDeleteDiscrepancyRowsChange(checked) {
      this.deleteDiscrepancyRows = false;
      if (!checked) return;
      const ids = this.discrepancyRecords.map((r) => r.id);
      if (!ids.length) return;
      this.openConfirm({
        title: this.$t(`${B}.deleteRowsWithIssues`),
        message: this.$t(`${B}.deleteDiscrepancyConfirm`, { count: ids.length }),
        confirmBtnText: this.$t("delete"),
        handler: () => this.performDelete(ids),
      });
    },
    async performDelete(ids) {
      if (!this.bulkImportId || !ids.length) return;
      this.actionLoading = true;
      try {
        const res = await ProcessingBatchBulkImportService.deleteRecords(this.bulkImportId, ids);
        if (res && res.success !== false) {
          const idSet = new Set(ids);
          this.detail.records = this.detail.records.filter((r) => !idSet.has(r.id));
          this.refreshDetailCounts();
          if (this.resultFilter !== RESULT_FILTER.ALL && this.resultFilter !== RESULT_FILTER.SUCCESS) {
            if (!this.errorCodeCounts[this.resultFilter]) this.resultFilter = RESULT_FILTER.ALL;
          }
          this.$notify({
            type: "success",
            text: res.message || this.$t(`${B}.recordsDeletedSuccess`),
          });
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.actionLoading = false;
      }
    },

    // ── Cancel import ───────────────────────────────────────────────────
    cancelImportAction() {
      if (!this.bulkImportId) {
        this.goBack();
        return;
      }
      this.openConfirm({
        title: this.$t(`${B}.cancelImportTitle`),
        message: this.$t(`${B}.cancelImportConfirm`),
        confirmBtnText: this.$t(`${B}.cancelImportConfirmBtn`),
        handler: () => this.performCancel(),
      });
    },
    async performCancel() {
      this.actionLoading = true;
      try {
        const res = await ProcessingBatchBulkImportService.cancelImport(this.bulkImportId);
        if (res && res.success !== false) {
          if (this.detail) this.detail.status = SERVER_BULK_IMPORT_STATUS.CANCELLED;
          this.$notify({
            type: "success",
            text: res.message || this.$t(`${B}.importCancelledSuccess`),
          });
          this.goBack();
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.actionLoading = false;
      }
    },

    // ── Run import ──────────────────────────────────────────────────────
    runImportAction() {
      if (!this.canImport) return;
      this.openConfirm({
        title: this.$t("oma.import"),
        message: this.$t(`${B}.importConfirm`, { count: this.successRecordsCount }),
        confirmBtnText: this.$t("oma.import"),
        handler: () => this.performImport(),
      });
    },
    async performImport() {
      this.importLoading = true;
      try {
        const res = await ProcessingBatchBulkImportService.runImport(this.bulkImportId);
        if (res && res.success !== false) {
          if (this.detail) this.detail.status = SERVER_BULK_IMPORT_STATUS.SUCCESS;
          this.importedCount =
            res.data && res.data.importedCount != null
              ? res.data.importedCount
              : this.successRecordsCount;
          this.successDialog = true;
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.importLoading = false;
      }
    },
    onSuccessClose() {
      this.successDialog = false;
      this.goBack();
    },

    // ── Confirm dialog plumbing ─────────────────────────────────────────
    openConfirm({ title, message, confirmBtnText, handler }) {
      this.confirm = {
        dialog: true,
        title: title || "",
        message: message || "",
        confirmBtnText: confirmBtnText || this.$t("confirm"),
        handler: handler || null,
      };
    },
    onConfirmCancel() {
      this.confirm.dialog = false;
      this.confirm.handler = null;
    },
    onConfirmContinue() {
      const handler = this.confirm.handler;
      this.confirm.dialog = false;
      this.confirm.handler = null;
      if (typeof handler === "function") handler();
    },
    notifyError(message) {
      this.$notify({
        type: "error",
        text: message || this.$t(`${B}.genericError`),
      });
    },
    goBack() {
      this.$router.push({ name: "ProcessingBatchBulkImport" });
    },
  },
};
</script>

<style lang="scss" scoped>
.bulk-result-progress-title {
  font-size: 14px;
}

.bulk-result-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bulk-result-progress-bar {
  flex: 1 1 auto;
}

.bulk-result-progress-percent {
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  min-width: 40px;
  text-align: right;
}

.bulk-result-progress-status {
  font-size: 14px;
}

.bulk-result-summary-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 20px 20px 20px 24px;
  border-left: 6px solid transparent;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.bulk-result-summary-card__value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1.1;
}

.bulk-result-summary-card__label {
  font-size: 14px;
  margin-top: 4px;
}

.bulk-result-filter-chips {
  gap: 12px;
}

.bulk-result-filter-chip {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.bulk-result-issues-alert {
  border: 1px solid;
  border-radius: 8px;
  padding: 20px 24px;
}

.bulk-result-issues-alert__heading {
  font-weight: 700;
  font-size: 15px;
}

.bulk-result-issues-alert__body {
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 0;
}

.bulk-result-issues-alert__checkbox ::v-deep .v-input--selection-controls__input {
  margin-right: 8px;
}

.bulk-result-table-card {
  max-height: 520px;
  overflow: auto;
}

.bulk-result-table ::v-deep tbody tr {
  cursor: pointer;
}

.bulk-result-table ::v-deep thead th span {
  text-transform: uppercase;
  font-weight: 600;
}

.bulk-result-status-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}

.non-clickable {
  pointer-events: none;
  cursor: default;
}

.bulk-result-table ::v-deep .bulk-result-action-col {
  white-space: nowrap;
}

.bulk-result-table ::v-deep td:last-child {
  white-space: nowrap;
}

.bulk-result-actions {
  gap: 4px;
}

.bulk-result-issue-tooltip {
  max-width: 340px;
  line-height: 1.45;
  font-size: 13px;
}
</style>
